import { gql } from '@apollo/client';
import * as Sentry from '@sentry/react-native';
import { useCallback } from 'react';
import uuid from 'react-native-uuid';

import { getReplyMessage, useApolloClient } from '@hooks/useApolloClient';
import { useChatNudge } from '@hooks/useChatNudge';
import useMe from '@hooks/useMe';
import {
  MemberTaskNudgeStep,
  useMemberTaskNudge,
} from '@hooks/useMemberTaskNudge';
import { OwnerNudgeStep, useOwnerNudge } from '@hooks/useOwnerNudge';
import { useTaskNudge } from '@hooks/useTaskNudge';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { bumpChatToTop } from '@src/utils/chat/bumpChatToTop';
import {
  hasFailedChatReadReceipt,
  requestChatMarkedRead,
} from '@src/utils/chat/chatReadGuard';
import { chatRowFieldsForSend } from '@src/utils/chat/chatRowFieldsForSend';
import { hydrateChatRowFields } from '@src/utils/chat/hydrateChatRowFields';
import { useMixpanel } from '@src/utils/mixpanel';

import {
  MessageTask,
  MessageProject,
  Document,
  MessageDetailFragmentDoc,
  DocumentDetailFragmentDoc,
  ListDraftDocumentsDocument,
  MessageEdge,
  Message,
  ListDraftMessagesDocument,
  ListDraftMessagesQuery,
  ListMessagesDocument,
  MarkChatsReadDocument,
} from '../graphql/generated';

export type SendDraftMessageOptions = {
  /**
   * TA-9295: true when the message is composed in the chat the user is
   * currently looking at (the thread composer), which is what makes it safe
   * to treat the chat as read. False/omitted for sends that target a chat
   * from outside it — forwarding, share-intent, the task bottom sheet.
   */
  fromOpenChat?: boolean;
};

const useSendMessage = () => {
  const { me } = useMe();
  const { client } = useApolloClient();
  const { cache } = client || {};
  const { trackEvent } = useMixpanel();
  const { completeStep: completeNudgeStep } = useOwnerNudge();
  const { completeNudge: completeChatNudge } = useChatNudge();
  const { activateNudge: activateTaskNudge } = useTaskNudge();
  const {
    taskId: memberNudgeTaskId,
    chatId: memberNudgeChatId,
    currentStep: memberNudgeCurrentStep,
    completeStep: completeMemberNudgeStep,
    setChatId: setMemberNudgeChatId,
  } = useMemberTaskNudge();

  const sendDraftMessage = useCallback(
    async (attributes: Message, options?: SendDraftMessageOptions) => {
      // TA-9295: only a message composed inside the open thread proves the
      // user has seen that chat. Forwarding a message or sharing into a chat
      // from outside it must not clear its unread badge.
      const fromOpenChat = options?.fromOpenChat ?? false;
      completeNudgeStep(OwnerNudgeStep.Chat);
      completeChatNudge();
      activateTaskNudge();
      if (memberNudgeCurrentStep === MemberTaskNudgeStep.SendMessage) {
        completeMemberNudgeStep(MemberTaskNudgeStep.SendMessage);
        setMemberNudgeChatId(attributes.chatId);
      }
      if (
        memberNudgeCurrentStep === MemberTaskNudgeStep.SendFirstChat &&
        attributes.chatId === memberNudgeChatId
      ) {
        completeMemberNudgeStep(MemberTaskNudgeStep.SendFirstChat);
      }
      const {
        chatId,
        clientId,
        attachments,
        replyId,
        replyMessage,
        checkin,
        failedAt,
      } = attributes;
      const messageTasks = (attachments || []).filter(
        (item) => item.__typename === 'MessageTask'
      ) as MessageTask[];
      const taskIds = messageTasks.map((item) => item.taskId);

      // Track task_comment_added event for each MessageTask
      messageTasks.forEach((messageTask) => {
        trackEvent(MIXPANEL_EVENTS.TASK_COMMENT_ADDED, {
          project_id: messageTask.projectId,
          task_id: messageTask.taskId,
        });
      });

      const messageProjects = (attachments || []).filter(
        (item) => item.__typename === 'MessageProject'
      ) as MessageProject[];
      const projectIds = messageProjects.map((item) => item.projectId);

      const documents = (attachments || []).filter(
        (item) => item.__typename === 'Document'
      ) as Document[];

      if (
        memberNudgeCurrentStep === MemberTaskNudgeStep.FirstUpdate &&
        memberNudgeTaskId &&
        taskIds.includes(memberNudgeTaskId)
      ) {
        completeMemberNudgeStep(MemberTaskNudgeStep.FirstUpdate);
      }

      if (
        memberNudgeCurrentStep === MemberTaskNudgeStep.AddFileToChat &&
        chatId === memberNudgeChatId &&
        documents.length > 0
      ) {
        completeMemberNudgeStep(MemberTaskNudgeStep.AddFileToChat);
      }

      let chatType: 'group' | 'direct' | undefined;
      if (cache) {
        try {
          const chatData = cache.readFragment<{ isGroupChat: boolean }>({
            id: cache.identify({ __typename: 'Chat', id: chatId }),
            fragment: gql`
              fragment ChatIsGroup on Chat {
                isGroupChat
              }
            `,
          });
          if (chatData?.isGroupChat !== undefined) {
            chatType = chatData.isGroupChat ? 'group' : 'direct';
          }
        } catch {
          // chat not in cache; chat_type will be omitted
        }
      }

      trackEvent(MIXPANEL_EVENTS.MESSAGE_SENT, {
        chat_id: chatId,
        chat_type: chatType,
        has_attachment:
          documents.length > 0 ||
          messageTasks.length > 0 ||
          messageProjects.length > 0,
      });

      const messageDocuments = documents.map((item) => {
        const newItem = {
          ...item,
          id: item.clientId,
          blobId: item.blobId,
          createdAt: new Date().toISOString(),
          projectIds,
          taskIds,
          owner: me,
          __typename: 'Document',
        };
        return newItem;
      });

      if (!cache) return undefined;

      messageDocuments.forEach((item) => {
        const newDocument = {
          id: cache.identify(item),
          fragment: DocumentDetailFragmentDoc,
          fragmentName: 'DocumentDetail',
          data: item,
        };
        cache.writeFragment(newDocument);
      });

      const createMessageId = cache.identify(attributes);
      const id = uuid.v4().toString();
      const replyMessage1 = replyMessage && getReplyMessage(replyMessage);
      const newMessage = {
        id: createMessageId,
        fragment: MessageDetailFragmentDoc,
        fragmentName: 'MessageDetail',
        data: {
          ...attributes,
          authorId: me?.id,
          authorName: me?.name,
          authorFirstName: me?.firstName,
          authorLastName: me?.lastName,
          authorEmail: me?.email,
          authorAvatarId: me?.avatarId || '',
          id,
          cursor: 'draft-cursor',
          replyId: replyId || null,
          replyMessage: replyMessage1 || null,
          attachments: [
            ...messageProjects,
            ...messageTasks,
            ...messageDocuments,
          ],
          reaction: [],
          failedAt: failedAt || null,
          checkin: checkin || null,
          userRequest: attributes.userRequest ?? null,
          // Fields the MessageDetail fragment expects but forward drafts may omit.
          // Without these, cache.writeFragment throws "Failed to match fragment".
          readAt: attributes.readAt ?? null,
          isRetry: attributes.isRetry ?? false,
          readReceipts: attributes.readReceipts ?? null,
        },
      };

      const newMessageRef = cache.writeFragment(newMessage);

      if (!newMessageRef) {
        Sentry.captureMessage(
          'useSendMessage: writeFragment returned undefined for chatId ' +
            JSON.stringify(chatId)
        );
      } else {
        cache.updateQuery(
          {
            query: ListMessagesDocument,
            variables: { chatId },
          },
          (data: any) => {
            const currentEdges: MessageEdge[] = data?.listMessages?.edges ?? [];
            const edgeExists = currentEdges.some(
              (edge: any) => edge?.node?.clientId === clientId
            );
            if (edgeExists) return data;

            const newEdge = {
              __typename: 'MessageEdge',
              cursor: uuid.v4().toString(),
              node: newMessage.data,
            };

            if (!data?.listMessages) {
              return {
                listMessages: {
                  __typename: 'MessageConnection',
                  edges: [newEdge],
                  pageInfo: {
                    __typename: 'PageInfo',
                    hasNextPage: true,
                    hasPreviousPage: false,
                    startCursor: '',
                    endCursor: '',
                  },
                },
              };
            }
            return {
              listMessages: {
                ...data.listMessages,
                edges: [...currentEdges, newEdge],
              },
            };
          }
        );
      }

      // Bump the chat to the top of the list ourselves rather than relying
      // solely on the `chatUpdate` subscription echoing our own message back
      // — that socket round-trip can be dropped/delayed (TA-10383).
      //
      // TA-10878: the return value is not a reliable success signal here and
      // is left unused, but the guard behind it has to be able to pass. It
      // could not for a chat below the first page: GetChatDetail caches five
      // of the row's fields only under nested names, so the bump refused to
      // prepend and the chat neither moved to the top nor showed its new
      // message. hydrateChatRowFields copies them across first. A refetch is
      // not the fallback at this point — the message is still a local draft,
      // so the server's answer would not contain it; the chatUpdate handler
      // refetches once the send has actually landed.
      // TA-9295: unreadCount 0 — the user is sending from inside this chat,
      // so its newest message is theirs and it cannot carry an unread badge.
      // Without this, an unread that was never cleared (typically a message
      // that arrived while the thread was open and whose `messageUpdate`
      // event was missed, TA-10150) kept its badge on a row now reading
      // "You: ...".
      // TA-10878: messageIcon is part of the row the same way messageBody is,
      // and it is always passed — a text message resolves to null, which is
      // what clears the paperclip/photo icon the previous attachment message
      // left on the row.
      hydrateChatRowFields(cache, chatId);

      const chatRowFields = chatRowFieldsForSend({
        body: attributes.body,
        createdAt: attributes.createdAt,
        attachments,
        fromOpenChat,
      });

      bumpChatToTop(cache, { chatId, ...chatRowFields, localClock: true });

      // The local zero above only holds until the next refetch — the unread
      // is real server-side until a read receipt is recorded, so tell the
      // server too. requestChatMarkedRead coalesces a burst into at most one
      // in-flight request plus one follow-up (TA-9951, Sentry Consecutive
      // HTTP) instead of dropping sends: every send must end up covered by a
      // receipt, or a message that landed between two rapid replies stays
      // unread on the server while the cache shows 0.
      //
      // hasFailedChatReadReceipt covers the offline case: this function only
      // stages the draft, and useChatProcessing re-invokes it (without
      // options) once the device is back online — long after RetryLink gave
      // up on the receipt that was fired while offline. Re-issuing it there
      // is what stops the badge from coming back when the message finally
      // reaches the server.
      const needsReceipt = fromOpenChat || hasFailedChatReadReceipt(chatId);
      if (needsReceipt && client) {
        requestChatMarkedRead(chatId, () =>
          client.mutate({
            mutation: MarkChatsReadDocument,
            variables: { attributes: { chatIds: [chatId] } },
          })
        );
      }

      cache.updateQuery<ListDraftMessagesQuery>(
        { query: ListDraftMessagesDocument },
        (data: any) => {
          const existing = data?.listDraftMessages || [];
          // Defensive dedupe — prevents the effect loop from processing the
          // same draft twice if sendDraftMessage is accidentally called again.
          if (existing.some((m: any) => m?.clientId === clientId)) {
            return data;
          }
          const newMessage = {
            ...attributes,
            // TA-10878: this query selects `chat { ...ChatDetail }`, so the
            // chat object the composer captured BEFORE the send is written
            // back onto the Chat entity and undoes the bump above. Carrying
            // the same row fields the bump just wrote keeps the two writes
            // from fighting — without this, the row kept the previous
            // message's attachment icon (the subscription repaired the body
            // but carries no messageIcon, so only the icon stayed wrong).
            ...(attributes.chat
              ? { chat: { ...attributes.chat, ...chatRowFields } }
              : {}),
            authorId: me?.id,
            authorName: me?.name,
            authorFirstName: me?.firstName,
            authorLastName: me?.lastName,
            authorEmail: me?.email,
            authorAvatarId: me?.avatarId ?? '',
            replyMessage: attributes.replyMessage || null,
            replyId: attributes.replyId || null,
            failedAt: attributes.failedAt || null,
            checkin: attributes.checkin || null,
            userRequest: attributes.userRequest ?? null,
            readAt: attributes.readAt ?? null,
            isRetry: attributes.isRetry ?? false,
            readReceipts: attributes.readReceipts ?? null,
          };
          return {
            listDraftMessages: [...existing, newMessage],
          };
        }
      );

      if (messageDocuments.length) {
        const draftDocuments = messageDocuments.map((m) => {
          return {
            ...m,
            file: { ...m.file, path: m.file.url },
            messageClientId: attributes.clientId,
            messageCreateYn: false,
          };
        });
        cache.updateQuery(
          {
            query: ListDraftDocumentsDocument,
            variables: {
              chatId: chatId,
            },
          },
          (data) => ({
            listDraftDocuments: [
              ...(data?.listDraftDocuments || []),
              ...draftDocuments,
            ],
          })
        );
      }
    },
    [
      cache,
      client,
      me,
      trackEvent,
      completeNudgeStep,
      completeChatNudge,
      activateTaskNudge,
      memberNudgeCurrentStep,
      memberNudgeTaskId,
      completeMemberNudgeStep,
    ]
  );

  return { sendDraftMessage };
};

export default useSendMessage;
